import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Switch,
  InputNumber,
  Row,
  Col,
  Statistic,
  Tabs,
  Tooltip,
  Popconfirm,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PercentageOutlined,
  DollarOutlined,
  CalculatorOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { Title } = Typography;
const { Option } = Select;

// User type tag colors
const userTypeColors = {
  customer: 'blue',
  provider: 'green',
  delivery: 'orange',
};

const userTypeLabels = {
  customer: 'Customer',
  provider: 'Provider',
  delivery: 'Delivery',
};

// Order Commissions Tab Component
const OrderCommissionsTab = () => {
  const { message } = App.useApp();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [commissionModalVisible, setCommissionModalVisible] = useState(false);
  const [commissionForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch all users for the user dropdown
  const { data: allUsers = [] } = useQuery(
    'all-users',
    async () => {
      try {
        const response = await api.get('/admin/users/list');
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch users:', error);
        return [];
      }
    },
    {
      staleTime: 10 * 60 * 1000,
    },
  );

  // Fetch orders with commission data
  const {
    data: orders = [],
    isLoading,
    refetch,
  } = useQuery(
    'order-commissions',
    async () => {
      try {
        const response = await api.get('/admin/orders?per_page=100');
        let orders = response.data?.data || response.data || [];
        orders = orders.filter((order) => order.order_status !== 'completed');
        return orders;
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 60000,
      staleTime: 2 * 60 * 1000,
    },
  );

  // Set commission mutation
  const setCommissionMutation = useMutation(
    async ({ orderId, user_id, commissions }) => {
      const response = await api.post(`/admin/orders/${orderId}/set-commission`, {
        user_id,
        commissions,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Commission set successfully');
        setCommissionModalVisible(false);
        setSelectedOrder(null);
        commissionForm.resetFields();
        refetch();
        queryClient.invalidateQueries('order-commissions');
      },
      onError: (error) => {
        message.error(
          'Failed to set commission: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const handleSetCommission = (order) => {
    setSelectedOrder(order);
    const formValues = {
      user_id: order.provider?.id || order.provider_id || order.user_id || undefined,
    };
    if (order.items && order.items.length > 0) {
      formValues.commissions = order.items.map((item) => ({
        order_item_id: item.id,
        commission_type: item.commission_type || 'percentage',
        commission_rate: item.commission_rate || 0,
        fixed_commission: item.fixed_commission || 0,
        commission_amount: item.commission_amount || 0,
      }));
    }
    commissionForm.setFieldsValue(formValues);
    setCommissionModalVisible(true);
  };

  const handleCommissionSubmit = async () => {
    try {
      const values = await commissionForm.validateFields();
      setCommissionMutation.mutate({
        orderId: selectedOrder.id,
        user_id: values.user_id,
        commissions: values.commissions,
      });
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Provider',
      dataIndex: 'provider_name',
      key: 'provider_name',
      render: (_, record) => {
        const name =
          record.provider_name ||
          record.provider?.name ||
          record.provider?.full_name ||
          'Unknown Provider';
        return name;
      },
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Order Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          confirmed: 'blue',
          preparing: 'purple',
          ready: 'cyan',
          on_the_way: 'geekblue',
          out_for_delivery: 'geekblue',
          delivered: 'green',
          cancelled: 'red',
          completed: 'purple',
        };
        return (
          <Tag color={colors[status]}>{status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}</Tag>
        );
      },
    },
    {
      title: 'Commission Status',
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      render: (amount, record) => {
        const hasCommission = record.items?.some((item) => item.commission_rate > 0);
        return hasCommission ? <Tag color="green">Set</Tag> : <Tag color="orange">Not Set</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const statusesThatDontNeedCommission = ['delivered', 'cancelled', 'completed'];
        const needsCommission = !statusesThatDontNeedCommission.includes(record.order_status);

        return (
          <Space>
            {needsCommission ? (
              <Button
                type="primary"
                size="small"
                onClick={() => handleSetCommission(record)}
                title="Set commission for this order"
              >
                Set Commission
              </Button>
            ) : (
              <Button
                size="small"
                onClick={() => handleSetCommission(record)}
                title={`Correct commission for ${record.order_status?.replace('_', ' ').toUpperCase()} order`}
              >
                Correct Commission
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Order Commissions
        </Title>
      </div>

      <Table
        columns={orderColumns}
        dataSource={orders}
        rowKey="id"
        loading={isLoading}
        pagination={{
          total: orders.length,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
        }}
        scroll={{ x: 1200 }}
      />

      {/* Commission Modal */}
      <Modal
        title={`${selectedOrder && ['delivered', 'cancelled', 'completed'].includes(selectedOrder.order_status) ? 'Correct' : 'Set'} Commission - Order ${selectedOrder?.order_number || ''}`}
        open={commissionModalVisible}
        onCancel={() => {
          setCommissionModalVisible(false);
          setSelectedOrder(null);
          commissionForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setCommissionModalVisible(false);
              setSelectedOrder(null);
              commissionForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={setCommissionMutation.isLoading}
            onClick={handleCommissionSubmit}
          >
            {selectedOrder &&
            ['delivered', 'cancelled', 'completed'].includes(selectedOrder.order_status)
              ? 'Correct Commission'
              : 'Set Commission'}
          </Button>,
        ]}
        width={800}
      >
        <Form form={commissionForm} layout="vertical">
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select user"
              showSearch
              filterOption={(input, option) =>
                option.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {allUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.name || user.full_name} ({userTypeLabels[user.user_type] || user.user_type})
                  - {user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.List name="commissions">
            {(fields, { add, remove: _remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      marginBottom: 16,
                      padding: 16,
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'commission_type']}
                          label="Commission Type"
                          rules={[{ required: true, message: 'Please select' }]}
                        >
                          <Select placeholder="Select type">
                            <Option value="percentage">Percentage</Option>
                            <Option value="fixed">Fixed Amount</Option>
                            <Option value="hybrid">Hybrid</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Conditional fields based on commission_type */}
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) =>
                          prev?.commissions?.[name]?.commission_type !==
                          curr?.commissions?.[name]?.commission_type
                        }
                      >
                        {({ getFieldValue }) => {
                          const cType = getFieldValue(['commissions', name, 'commission_type']);

                          if (cType === 'fixed') {
                            return (
                              <>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'fixed_commission']}
                                    label="Fixed Amount (₨)"
                                    rules={[{ required: true, message: 'Please enter amount' }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      placeholder="0.00"
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'commission_amount']}
                                    label="Commission Amt (₨)"
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      placeholder="Auto-calc"
                                      disabled
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            );
                          } else if (cType === 'hybrid') {
                            return (
                              <>
                                <Col span={5}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'commission_rate']}
                                    label="Rate (%)"
                                    rules={[{ required: true, message: 'Please enter rate' }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      max={100}
                                      precision={2}
                                      placeholder="0.00"
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={5}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'fixed_commission']}
                                    label="Fixed (₨)"
                                    rules={[{ required: true, message: 'Please enter amount' }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      placeholder="0.00"
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'commission_amount']}
                                    label="Total (₨)"
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      placeholder="Auto"
                                      disabled
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            );
                          } else if (cType === 'percentage') {
                            return (
                              <>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'commission_rate']}
                                    label="Commission Rate (%)"
                                    rules={[{ required: true, message: 'Please enter rate' }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      max={100}
                                      precision={2}
                                      placeholder="0.00"
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'commission_amount']}
                                    label="Commission Amt (₨)"
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      placeholder="Auto-calc"
                                      disabled
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            );
                          } else {
                            // No type selected yet - show hint
                            return (
                              <Col span={14}>
                                <div style={{ padding: '8px 0', color: '#999' }}>
                                  Select a commission type to configure
                                </div>
                              </Col>
                            );
                          }
                        }}
                      </Form.Item>
                    </Row>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Commission Item
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

const CommissionManagement = () => {
  const { message } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCorrectionModalVisible, setIsCorrectionModalVisible] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [form] = Form.useForm();
  const [correctionForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch commissions
  const { data: commissions = [], isLoading: commissionsLoading } = useQuery(
    'commissions',
    async () => {
      try {
        const response = await api.get('/admin/commissions');
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch commissions:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch commission statistics
  const { data: stats = {}, isLoading: statsLoading } = useQuery(
    'commission-stats',
    async () => {
      try {
        const response = await api.get('/admin/commissions/statistics');
        return response.data?.data || response.data || {};
      } catch (error) {
        console.error('Failed to fetch commission stats:', error);
        return {};
      }
    },
    {
      refetchInterval: 60000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Create/Update commission mutation
  const commissionMutation = useMutation(
    async (data) => {
      if (editingCommission) {
        const response = await api.put(`/admin/commissions/${editingCommission.id}`, data);
        return response.data;
      } else {
        const response = await api.post('/admin/commissions', data);
        return response.data;
      }
    },
    {
      onSuccess: () => {
        message.success(`Commission ${editingCommission ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        setEditingCommission(null);
        form.resetFields();
        queryClient.invalidateQueries('commissions');
        queryClient.invalidateQueries('commission-stats');
      },
      onError: (error) => {
        message.error(
          error.response?.data?.message ||
            `Failed to ${editingCommission ? 'update' : 'create'} commission`,
        );
      },
    },
  );

  // Delete commission mutation
  const deleteCommissionMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/admin/commissions/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Commission deleted successfully');
        queryClient.invalidateQueries('commissions');
        queryClient.invalidateQueries('commission-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete commission');
      },
    },
  );

  // Correct commission mutation
  const correctCommissionMutation = useMutation(
    async (data) => {
      const response = await api.post('/admin/commissions/correct', data);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Commission corrected successfully');
        setIsCorrectionModalVisible(false);
        correctionForm.resetFields();
        queryClient.invalidateQueries('commissions');
        queryClient.invalidateQueries('commission-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to correct commission');
      },
    },
  );

  // Fetch all users for dropdown (not just providers)
  const { data: allUsers = [], isLoading: usersLoading } = useQuery(
    'all-users-for-commissions',
    async () => {
      try {
        const response = await api.get('/admin/users/list');
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch users:', error);
        return [];
      }
    },
    {
      staleTime: 10 * 60 * 1000,
    },
  );

  // Check if any commission exists (not just active ones)
  const hasAnyCommission = commissions.length > 0;

  // Available users = users without existing commission rules
  const availableUsers = allUsers.filter(
    (user) =>
      !commissions.some((commission) => {
        const commissionUserId = commission.user?.id || commission.user_id;
        return commissionUserId === user.id;
      }),
  );

  const handleCreate = () => {
    if (availableUsers.length === 0) {
      message.warning(
        'All users already have commission rules. Please edit existing commissions instead.',
      );
      return;
    }
    setEditingCommission(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (commission) => {
    setEditingCommission(commission);
    form.setFieldsValue({
      user_id: commission.user?.id || commission.user_id,
      user_type: commission.user_type || commission.user?.user_type || 'customer',
      commission_rate: commission.commission_rate,
      fixed_commission: commission.fixed_commission,
      commission_type: commission.commission_type,
      is_active: commission.is_active,
      notes: commission.notes,
    });
    setIsModalVisible(true);
  };

  const handleCorrection = () => {
    correctionForm.resetFields();
    setIsCorrectionModalVisible(true);
  };

  const handleSubmitCorrection = async (values) => {
    try {
      correctCommissionMutation.mutate(values);
    } catch (error) {
      console.error('Correction error:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      commissionMutation.mutate(values);
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleDelete = (id) => {
    deleteCommissionMutation.mutate(id);
  };

  const handleToggleStatus = (id, isActive) => {
    commissionMutation.mutate({ id, is_active: isActive });
  };

  const columns = [
    {
      title: 'User',
      dataIndex: ['user', 'name'],
      key: 'user',
      render: (name, record) => {
        const user = record.user || {};
        const displayName = name || user.name || user.email || 'Unknown';
        const userType = record.user_type || user.user_type || 'customer';
        return (
          <div>
            <div style={{ fontWeight: '500' }}>{displayName}</div>
            <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
            <Tag color={userTypeColors[userType]} style={{ marginTop: 4 }}>
              {userTypeLabels[userType] || userType}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Commission Type',
      dataIndex: 'commission_type',
      key: 'commission_type',
      render: (type) => {
        const colors = {
          percentage: 'blue',
          fixed: 'green',
          hybrid: 'purple',
        };
        return <Tag color={colors[type]}>{type?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Commission Rate',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      render: (rate, record) => {
        if (record.commission_type === 'percentage') {
          return <span style={{ color: '#1890ff' }}>{rate}%</span>;
        } else if (record.commission_type === 'fixed') {
          return <span style={{ color: '#52c41a' }}>₨{record.fixed_commission}</span>;
        } else {
          return (
            <div>
              <div style={{ color: '#1890ff' }}>{rate}%</div>
              <div style={{ color: '#52c41a', fontSize: '14px' }}>+ ₨{record.fixed_commission}</div>
            </div>
          );
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (notes) => (
        <Tooltip title={notes}>
          <span>{notes || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={record.is_active ? 'Deactivate Commission' : 'Activate Commission'}>
            <Switch
              size="small"
              checked={record.is_active}
              onChange={(checked) => handleToggleStatus(record.id, checked)}
              loading={commissionMutation.isLoading}
            />
          </Tooltip>
          <Tooltip title="Edit Commission">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this commission?"
            description="This will permanently remove this commission rule."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Commission">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>
        <PercentageOutlined /> Commission Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Users with Commission"
              value={stats.total_users_with_commission || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Percentage Based"
              value={stats.percentage_based || 0}
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#1890ff' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Fixed Based"
              value={stats.fixed_based || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Commission Collected"
              value={stats.total_commission_collected || 0}
              prefix={<CalculatorOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* User Type Breakdown */}
      {stats.by_user_type && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Provider Commissions"
                value={stats.by_user_type.provider || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#52c41a', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Customer Commissions"
                value={stats.by_user_type.customer || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Delivery Commissions"
                value={stats.by_user_type.delivery || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#fa8c16', fontSize: 24 }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Tabs defaultActiveKey="commission-rules">
          <Tabs.TabPane tab="Commission Rules" key="commission-rules">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Commission Rules
              </Title>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  disabled={hasAnyCommission && availableUsers.length === 0}
                >
                  Add Commission Rule
                </Button>
                <Button type="default" icon={<SyncOutlined />} onClick={handleCorrection}>
                  Correct Commission
                </Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={commissions}
              rowKey="id"
              loading={commissionsLoading}
              pagination={{
                total: commissions.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} commission rules`,
              }}
              scroll={{ x: 1000 }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Order Commissions" key="order-commissions">
            <OrderCommissionsTab />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Commission Form Modal */}
      <Modal
        title={`${editingCommission ? 'Edit' : 'Add'} Commission Rule`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCommission(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select user"
              showSearch
              loading={usersLoading}
              filterOption={(input, option) =>
                option.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={!!editingCommission}
            >
              {availableUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.name || user.full_name} ({userTypeLabels[user.user_type] || user.user_type})
                  - {user.email}
                </Option>
              ))}
              {availableUsers.length === 0 && !editingCommission && (
                <Option disabled value="no-users">
                  All users already have commission rules
                </Option>
              )}
            </Select>
          </Form.Item>

          {/* Show user_type when editing */}
          {editingCommission && (
            <Form.Item name="user_type" label="User Type">
              <Select placeholder="User type (auto-detected)" disabled>
                <Option value="customer">Customer</Option>
                <Option value="provider">Provider</Option>
                <Option value="delivery">Delivery</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="commission_type"
            label="Commission Type"
            rules={[{ required: true, message: 'Please select commission type' }]}
          >
            <Select placeholder="Select commission type">
              <Option value="percentage">Percentage</Option>
              <Option value="fixed">Fixed Amount</Option>
              <Option value="hybrid">Hybrid (Percentage + Fixed)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.commission_type !== currentValues.commission_type
            }
          >
            {({ getFieldValue }) => {
              const commissionType = getFieldValue('commission_type');
              return (
                <>
                  {(commissionType === 'percentage' || commissionType === 'hybrid') && (
                    <Form.Item
                      name="commission_rate"
                      label="Commission Rate (%)"
                      rules={[
                        { required: true, message: 'Please enter commission rate' },
                        {
                          type: 'number',
                          min: 0,
                          max: 100,
                          message: 'Rate must be between 0 and 100',
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Enter commission rate"
                        min={0}
                        max={100}
                        precision={2}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value.replace('%', '')}
                      />
                    </Form.Item>
                  )}
                  {(commissionType === 'fixed' || commissionType === 'hybrid') && (
                    <Form.Item
                      name="fixed_commission"
                      label="Fixed Commission Amount (₨)"
                      rules={[
                        { required: true, message: 'Please enter fixed commission amount' },
                        { type: 'number', min: 0, message: 'Amount must be positive' },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Enter fixed amount"
                        min={0}
                        precision={2}
                        formatter={(value) => `₨${value}`}
                        parser={(value) => value.replace(/₨\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  )}
                  <Form.Item name="is_active" label="Status" valuePropName="checked">
                    <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                  </Form.Item>
                  <Form.Item
                    name="notes"
                    label="Notes"
                    rules={[{ required: false, message: 'Please enter notes' }]}
                  >
                    <Input.TextArea rows={3} placeholder="Enter notes" maxLength={500} showCount />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingCommission(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={commissionMutation.isLoading}>
                {editingCommission ? 'Update' : 'Create'} Commission
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Commission Correction Modal */}
      <Modal
        title="Correct Commission"
        open={isCorrectionModalVisible}
        onCancel={() => {
          setIsCorrectionModalVisible(false);
          correctionForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={correctionForm} layout="vertical" onFinish={handleSubmitCorrection}>
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select user"
              showSearch
              loading={usersLoading}
              filterOption={(input, option) =>
                option.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {commissions.map((commission) => {
                const user = commission.user || {};
                return (
                  <Option key={commission.id} value={user.id}>
                    {user.name || user.full_name} (
                    {userTypeLabels[commission.user_type || user.user_type] || 'N/A'}) -{' '}
                    {user.email}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="order_id"
            label="Order ID"
            rules={[{ required: true, message: 'Please enter order ID' }]}
          >
            <Input
              style={{ width: '100%' }}
              placeholder="Enter order ID to correct commission for"
            />
          </Form.Item>
          <Form.Item
            name="new_commission_rate"
            label="New Commission Rate (%)"
            rules={[{ required: true, message: 'Please enter new commission rate' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter new commission rate"
              min={0}
              max={100}
              precision={2}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Correction Reason"
            rules={[{ required: true, message: 'Please enter correction reason' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Ex: Product price was set incorrectly, commission should be 3% instead of 5%"
              maxLength={255}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsCorrectionModalVisible(false);
                  correctionForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={correctCommissionMutation.isLoading}
              >
                Correct Commission
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CommissionManagement;
